import { useState, useCallback } from 'react';
import { Button, Input, Select, Badge, Pagination, LoadingSkeleton } from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import { useAuth } from '../../shared/auth/AuthContext';
import { useErrorLogs, type ErrorLog, type ErrorLevel } from './useErrorLogs';
import '../../../../../packages/design-system/src/styles/premium.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'medium',
    });
  } catch {
    return iso;
  }
}

const levelLabel: Record<ErrorLevel, string> = {
  error: 'ERROR',
  warn: 'WARN',
  info: 'INFO',
  debug: 'DEBUG',
};

const levelDotColor: Record<ErrorLevel, string> = {
  error: '#ba1a1a',
  warn: '#ed6c02',
  info: '#1a365d',
  debug: '#74777f',
};

const SERVICE_OPTIONS = [
  { value: 'ALL', label: 'Todos os serviços' },
  { value: 'api', label: 'api' },
  { value: 'worker', label: 'worker' },
  { value: 'fiscal', label: 'fiscal' },
  { value: 'webhook', label: 'webhook' },
  { value: 'integration', label: 'integration' },
  { value: 'auth', label: 'auth' },
];

const LEVEL_OPTIONS = [
  { value: 'ALL', label: 'Todos os níveis' },
  { value: 'error', label: 'ERROR' },
  { value: 'warn', label: 'WARN' },
  { value: 'info', label: 'INFO' },
  { value: 'debug', label: 'DEBUG' },
];

const PERIOD_OPTIONS = [
  { value: 'ALL', label: 'Todo o período' },
  { value: '24h', label: 'Últimas 24h' },
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: 'custom', label: 'Personalizado' },
];

// ── Icons (inline SVG — no extra dep) ───────────────────────────────────────

function IconCopy({ size = 14 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v3" />
    </svg>
  );
}
function IconChevron({ open, size = 16 }: { open: boolean; size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 180ms var(--premium-motion, ease)' }}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
function IconSearch({ size = 16 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
function IconLogs({ size = 20 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

// ── Row ──────────────────────────────────────────────────────────────────────

function ErrorLogRow({
  log,
  expanded,
  onToggle,
  onCopyTrace,
}: {
  log: ErrorLog;
  expanded: boolean;
  onToggle: () => void;
  onCopyTrace: (traceId: string) => void;
}): JSX.Element {
  return (
    <>
      <tr
        className="obs-row"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`Log ${log.level} ${log.service} — ${log.message.slice(0, 60)}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <td className="cell-time" style={{ whiteSpace: 'nowrap', fontSize: '0.8125rem', color: '#c4c6cf' }}>
          {formatDateTime(log.timestamp)}
        </td>
        <td>
          <span
            className="level-pill"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.125rem 0.625rem',
              borderRadius: '999px',
              fontSize: '0.6875rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              border: `1px solid ${levelDotColor[log.level]}44`,
              background: `${levelDotColor[log.level]}18`,
              color: levelDotColor[log.level],
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: levelDotColor[log.level],
                boxShadow: `0 0 8px ${levelDotColor[log.level]}88`,
                flexShrink: 0,
              }}
            />
            {levelLabel[log.level] ?? log.level}
          </span>
        </td>
        <td>
          <Badge variant="neutral" size="sm">{log.service}</Badge>
        </td>
        <td className="cell-message" style={{ maxWidth: 420 }}>
          <span
            title={log.message}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontSize: '0.875rem',
              color: '#e0e3e5',
              lineHeight: 1.4,
            }}
          >
            {log.message}
          </span>
          {(log.httpMethod || log.httpStatus) && (
            <span style={{ display: 'inline-flex', gap: '0.375rem', marginTop: '0.25rem' }}>
              {log.httpMethod && <span style={{ fontSize: '0.6875rem', color: '#74777f', background: 'rgba(255,255,255,0.06)', padding: '0 0.375rem', borderRadius: 4 }}>{log.httpMethod}</span>}
              {log.httpStatus && <span style={{ fontSize: '0.6875rem', color: log.httpStatus >= 500 ? '#ffb4ab' : '#f2bc82', background: 'rgba(255,255,255,0.06)', padding: '0 0.375rem', borderRadius: 4 }}>{log.httpStatus}</span>}
              {log.httpPath && <span style={{ fontSize: '0.6875rem', color: '#74777f', fontFamily: 'monospace' }}>{log.httpPath}</span>}
            </span>
          )}
        </td>
        <td className="cell-trace">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
            <code
              style={{
                fontSize: '0.75rem',
                color: '#adc7f7',
                background: 'rgba(0, 32, 69, 0.10)',
                border: '1px solid rgba(0, 32, 69, 0.18)',
                padding: '0.125rem 0.375rem',
                borderRadius: 6,
                maxWidth: 140,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
              }}
              title={log.traceId}
            >
              {log.traceId.slice(0, 8)}…{log.traceId.slice(-4)}
            </code>
            <button
              type="button"
              aria-label={`Copiar traceId ${log.traceId}`}
              title="Copiar traceId"
              onClick={(e) => {
                e.stopPropagation();
                onCopyTrace(log.traceId);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 26,
                height: 26,
                borderRadius: 6,
                border: '1px solid rgba(255,255,255,0.10)',
                background: 'rgba(255,255,255,0.06)',
                color: '#c4c6cf',
                cursor: 'pointer',
              }}
            >
              <IconCopy size={12} />
            </button>
          </span>
        </td>
        <td style={{ textAlign: 'center' }}>
          <span style={{ color: '#74777f', display: 'inline-flex' }}>
            <IconChevron open={expanded} />
          </span>
        </td>
      </tr>
      {expanded && (
        <tr className="obs-expand-row">
          <td colSpan={6} style={{ padding: 0, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div
              style={{
                padding: '1rem 1.25rem',
                background: 'rgba(8,11,18,0.55)',
                borderTop: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              {log.stack ? (
                <div style={{ marginBottom: log.metadata ? '1rem' : 0 }}>
                  <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#74777f', marginBottom: '0.5rem' }}>
                    Stack trace
                  </div>
                  <pre
                    style={{
                      margin: 0,
                      padding: '0.75rem 1rem',
                      background: 'rgba(25, 28, 30, 0.9)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 10,
                      fontSize: '0.8125rem',
                      lineHeight: 1.6,
                      overflow: 'auto',
                      maxHeight: 260,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: '#e0e3e5',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                  >
                    {log.stack}
                  </pre>
                </div>
              ) : null}
              {log.metadata && Object.keys(log.metadata).length > 0 ? (
                <div>
                  <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#74777f', marginBottom: '0.5rem' }}>
                    Metadata (JSON)
                  </div>
                  <pre
                    style={{
                      margin: 0,
                      padding: '0.75rem 1rem',
                      background: 'rgba(25, 28, 30, 0.9)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 10,
                      fontSize: '0.8125rem',
                      lineHeight: 1.6,
                      overflow: 'auto',
                      maxHeight: 260,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: '#d6e3ff',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                  >
                    {JSON.stringify(log.metadata, null, 2)}
                  </pre>
                </div>
              ) : !log.stack ? (
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#74777f' }}>Nenhum detalhe adicional para este log.</p>
              ) : null}
              <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', color: '#43474e' }}>ID: <code style={{ color: '#c4c6cf' }}>{log.id}</code></span>
                {log.requestId && <span style={{ fontSize: '0.75rem', color: '#43474e' }}>requestId: <code style={{ color: '#c4c6cf' }}>{log.requestId}</code></span>}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ErrorLogsPage(): JSX.Element {
  const { addToast } = useToast();
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin') || hasRole('manager') || hasRole('owner');

  const [level, setLevel] = useState('ALL');
  const [service, setService] = useState('ALL');
  const [period, setPeriod] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const pageSize = 20;

  const filters = {
    ...(level !== 'ALL' ? { level } : {}),
    ...(service !== 'ALL' ? { service } : {}),
    ...(period !== 'ALL' ? { period } : {}),
    ...(period === 'custom' && dateFrom ? { dateFrom } : {}),
    ...(period === 'custom' && dateTo ? { dateTo } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
    page,
    limit: pageSize,
  };

  const { data, isLoading, isFetching, error, refetch } = useErrorLogs(filters);

  const logs = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleSearch = useCallback(() => {
    setSearch(searchInput);
    setPage(1);
  }, [searchInput]);

  const handleClear = useCallback(() => {
    setLevel('ALL');
    setService('ALL');
    setPeriod('ALL');
    setDateFrom('');
    setDateTo('');
    setSearch('');
    setSearchInput('');
    setPage(1);
    setExpandedId(null);
  }, []);

  const hasActiveFilters =
    level !== 'ALL' || service !== 'ALL' || period !== 'ALL' || search.trim() !== '' || dateFrom !== '' || dateTo !== '';

  const handleCopyTrace = useCallback(
    async (traceId: string) => {
      try {
        await navigator.clipboard.writeText(traceId);
        addToast('traceId copiado', 'success');
      } catch {
        // fallback
        const ta = document.createElement('textarea');
        ta.value = traceId;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        addToast('traceId copiado', 'success');
      }
    },
    [addToast],
  );

  const toggleExpand = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  if (!isAdmin) {
    return (
      <div className="obs-page page-premium" style={{ minHeight: '100vh', padding: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="premium-card" style={{ padding: '2rem', textAlign: 'center', maxWidth: 480 }}>
          <p style={{ fontWeight: 700, color: '#f2f4f6', margin: '0 0 0.5rem' }}>Acesso restrito</p>
          <p style={{ fontSize: '0.875rem', color: '#74777f', margin: 0 }}>Esta área é visível apenas para administradores.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="obs-page page-premium" style={{ minHeight: '100vh', padding: '1.5rem 1.5rem 2.5rem' }}>
      {/* Header premium */}
      <div
        className="premium-card"
        style={{
          padding: '1.25rem 1.5rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
          background: 'linear-gradient(135deg, rgba(25, 28, 30, 0.72) 0%, rgba(8,11,18,0.72) 100%)',
        }}
      >
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
          <div
            aria-hidden="true"
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, rgba(0, 32, 69, 0.22) 0%, rgba(0, 32, 69, 0.22) 100%)',
              border: '1px solid rgba(0, 32, 69, 0.28)',
              color: '#1a365d',
              flexShrink: 0,
            }}
          >
            <IconLogs size={22} />
          </div>
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: '1.375rem',
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: '#f2f4f6',
                lineHeight: 1.2,
              }}
            >
              Logs de Erros <span className="text-gradient-aurora" style={{ fontWeight: 800 }}>· Observabilidade</span>
            </h1>
            <p style={{ margin: '0.375rem 0 0', fontSize: '0.875rem', color: '#74777f', maxWidth: 640, lineHeight: 1.5 }}>
              Rastreie falhas por nível, serviço e traceId. Expanda um log para ver stack trace e metadata JSON. Dados via <code style={{ color: '#adc7f7' }}>GET /api/v1/admin/error-logs</code> com fallback mock tipado.
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
          <span
            aria-live="polite"
            style={{
              fontSize: '0.8125rem',
              color: '#c4c6cf',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.08)',
              padding: '0.375rem 0.75rem',
              borderRadius: 999,
            }}
          >
            {isFetching && !isLoading ? 'Atualizando…' : `${total} registro${total !== 1 ? 's' : ''}`}
          </span>
          <Button variant="outline" size="sm" onClick={() => refetch()} aria-label="Recarregar logs">
            Recarregar
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div
        className="premium-card"
        style={{
          padding: '1rem',
          marginBottom: '1rem',
          background: 'rgba(25, 28, 30, 0.55)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 20,
          boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
        }}
      >
        <div
          role="search"
          aria-label="Filtros de logs"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.75rem',
            alignItems: 'flex-end',
          }}
        >
          <div style={{ minWidth: 160, flex: '0 0 180px' }}>
            <label htmlFor="obs-level" style={labelStyle}>Nível</label>
            <Select
              id="obs-level"
              value={level}
              onChange={(e) => {
                setLevel(e.target.value);
                setPage(1);
              }}
              aria-label="Filtrar por nível"
            >
              {LEVEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
          </div>

          <div style={{ minWidth: 160, flex: '0 0 180px' }}>
            <label htmlFor="obs-service" style={labelStyle}>Serviço</label>
            <Select
              id="obs-service"
              value={service}
              onChange={(e) => {
                setService(e.target.value);
                setPage(1);
              }}
              aria-label="Filtrar por serviço"
            >
              {SERVICE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
          </div>

          <div style={{ minWidth: 160, flex: '0 0 180px' }}>
            <label htmlFor="obs-period" style={labelStyle}>Período</label>
            <Select
              id="obs-period"
              value={period}
              onChange={(e) => {
                setPeriod(e.target.value);
                setPage(1);
              }}
              aria-label="Filtrar por período"
            >
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
          </div>

          {period === 'custom' && (
            <>
              <div style={{ minWidth: 140, flex: '0 0 160px' }}>
                <label htmlFor="obs-from" style={labelStyle}>De</label>
                <Input id="obs-from" type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} aria-label="Data inicial" />
              </div>
              <div style={{ minWidth: 140, flex: '0 0 160px' }}>
                <label htmlFor="obs-to" style={labelStyle}>Até</label>
                <Input id="obs-to" type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} aria-label="Data final" />
              </div>
            </>
          )}

          <div style={{ flex: '1 1 260px', minWidth: 220 }}>
            <label htmlFor="obs-search" style={labelStyle}>Busca por traceId / mensagem</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <span aria-hidden="true" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#43474e', display: 'inline-flex' }}>
                  <IconSearch size={14} />
                </span>
                <input
                  id="obs-search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                  placeholder="Ex: a1b2c3d4 ou 'database timeout'"
                  aria-label="Buscar por traceId ou mensagem"
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem 0.5rem 2rem',
                    borderRadius: 10,
                    border: '1px solid rgba(255,255,255,0.10)',
                    background: 'rgba(255,255,255,0.06)',
                    color: '#f2f4f6',
                    fontSize: '0.875rem',
                    outline: 'none',
                  }}
                />
              </div>
              <Button variant="primary" size="sm" onClick={handleSearch} aria-label="Buscar">
                Buscar
              </Button>
            </div>
          </div>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={handleClear} aria-label="Limpar filtros">
              Limpar
            </Button>
          )}
        </div>
      </div>

      {/* Conteúdo */}
      <div
        className="premium-card"
        style={{
          overflow: 'hidden',
          background: 'rgba(25, 28, 30, 0.55)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 20,
          boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
          padding: 0,
        }}
      >
        {/* Loading skeletons */}
        {isLoading && (
          <div style={{ padding: '1rem' }} aria-busy="true" aria-live="polite" aria-label="Carregando logs">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <LoadingSkeleton width={140} height="16px" borderRadius="6px" />
                  <LoadingSkeleton width={70} height="20px" borderRadius="999px" />
                  <LoadingSkeleton width={80} height="20px" borderRadius="10px" />
                  <LoadingSkeleton width="40%" height="16px" />
                  <LoadingSkeleton width={120} height="20px" borderRadius="6px" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error retry — backend real; sem fallback mock (todo18) */}
        {!isLoading && error && logs.length === 0 && (
          <div role="alert" style={{ padding: '2.5rem 1.5rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }} aria-hidden="true">⚠️</div>
            <p style={{ margin: '0 0 0.375rem', fontWeight: 700, color: '#f2f4f6' }}>Erro ao carregar logs</p>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#74777f' }}>
              {(error as Error).message || 'Falha ao buscar /api/v1/admin/error-logs'}
            </p>
            <Button variant="primary" onClick={() => refetch()} aria-label="Tentar novamente">
              Tentar novamente
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && logs.length === 0 && (
          <div style={{ padding: '3rem 1.5rem', textAlign: 'center' }} role="status" aria-live="polite">
            <div
              aria-hidden="true"
              style={{
                width: 64,
                height: 64,
                borderRadius: 16,
                margin: '0 auto 1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0, 32, 69, 0.10)',
                border: '1px solid rgba(0, 32, 69, 0.18)',
                color: '#1a365d',
              }}
            >
              <IconLogs size={28} />
            </div>
            <p style={{ margin: '0 0 0.375rem', fontWeight: 700, color: '#f2f4f6' }}>Nenhum log encontrado</p>
            <p style={{ margin: '0 auto 1rem', fontSize: '0.875rem', color: '#74777f', maxWidth: 420 }}>
              {hasActiveFilters
                ? 'Tente ajustar os filtros ou limpar a busca para ver mais resultados.'
                : 'Ainda não há logs de erro registrados neste ambiente.'}
            </p>
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={handleClear}>Limpar filtros</Button>
            )}
          </div>
        )}

        {/* Tabela */}
        {!isLoading && logs.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }} role="region" aria-label="Tabela de logs de erros" tabIndex={0}>
              <table
                className="obs-table"
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.875rem',
                }}
              >
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <th style={thStyle}>Timestamp</th>
                    <th style={thStyle}>Level</th>
                    <th style={thStyle}>Service</th>
                    <th style={thStyle}>Message</th>
                    <th style={thStyle}>TraceId</th>
                    <th style={{ ...thStyle, textAlign: 'center', width: 56 }} aria-label="Expandir"> </th>
                  </tr>
                </thead>
                <tbody aria-live="polite" aria-relevant="additions removals">
                  {logs.map((log) => (
                    <ErrorLogRow
                      key={log.id}
                      log={log}
                      expanded={expandedId === log.id}
                      onToggle={() => toggleExpand(log.id)}
                      onCopyTrace={handleCopyTrace}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
                padding: '0.875rem 1rem',
                borderTop: '1px solid rgba(255,255,255,0.06)',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: '0.8125rem', color: '#74777f' }}>
                Página {page} de {totalPages} · {total} total
              </span>
              <Pagination page={page} totalPages={totalPages} onChange={(p) => setPage(p)} />
            </div>
          </>
        )}

        {/* Fetching overlay sutil */}
        {isFetching && !isLoading && logs.length > 0 && (
          <div
            aria-hidden="true"
            style={{
              height: 2,
              background: 'linear-gradient(90deg, transparent, #1a365d, transparent)',
              backgroundSize: '200% 100%',
              animation: 'obs-shimmer 1.2s ease-in-out infinite',
            }}
          />
        )}
      </div>

      <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#43474e', textAlign: 'center' }}>
        Dica: clique em uma linha para expandir stack trace e metadata JSON. Use o traceId para correlação nos logs do backend.
      </p>

      <style>{`
        .obs-page .obs-table th { text-align: left; padding: 0.75rem 1rem; font-size: 0.6875rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #74777f; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .obs-page .obs-table td { padding: 0.875rem 1rem; border-bottom: 1px solid rgba(255,255,255,0.06); vertical-align: top; }
        .obs-page .obs-row { cursor: pointer; transition: background 160ms ease; }
        .obs-page .obs-row:hover { background: rgba(255,255,255,0.04); }
        .obs-page .obs-row:focus-visible { outline: 2px solid #1a365d; outline-offset: -2px; }
        @keyframes obs-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @media (prefers-reduced-motion: reduce) {
          .obs-page .obs-row, .obs-page .premium-card { transition: none !important; }
          .obs-page .obs-row:hover { transform: none; }
          @keyframes obs-shimmer { from { opacity: 1; } to { opacity: 1; } }
        }
      `}</style>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#74777f',
  marginBottom: '0.375rem',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.75rem 1rem',
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#74777f',
  whiteSpace: 'nowrap',
};

export default ErrorLogsPage;
